/**
 * Owner: Masa Bayu
 * File: admin-restricted-labels.ts
 * Date:Time: 2026-10-23 11:40
 * Description: §468 — label untuk dua permukaan TERHAD yang bukan sebahagian
 *   daripada console lima seksyen: halaman log masuk pentadbir dan skrin akaun
 *   pentadbir. Asalnya dititip ke `admin-labels.ts`, tetapi fail itu sudah 435
 *   baris dan had Owner ialah 499 baris per fail sumber, jadi ia berdiri sendiri
 *   dengan pola locale yang sama (en lalai / ms).
 */

import type { MarketplaceLanguage } from '../home/marketplace/marketplace-locale';

export interface AdminRestrictedLabels {
  // Halaman log masuk
  readonly loginTitle: string;
  readonly loginSubtitle: string;
  readonly loginBadge: string;
  readonly loginEmail: string;
  readonly loginPassword: string;
  readonly loginSubmit: string;
  readonly loginPending: string;
  readonly loginShow: string;
  readonly loginHide: string;
  readonly loginNoSignup: string;
  readonly loginNotAdmin: string;
  readonly loginFailed: string;
  readonly loginSessionNote: string;
  readonly loginBackStore: string;

  // Skrin akaun pentadbir
  readonly acctBadge: string;
  readonly acctConsoleTitle: string;
  readonly acctConsoleBody: string;
  readonly acctOpenConsole: string;
  readonly acctInsightTitle: string;
  readonly acctInsightBody: string;
  readonly acctSessionTitle: string;
  readonly acctSessionBody: string;
  readonly acctAccountId: string;
  readonly acctHub: string;
  readonly acctSignInMethod: string;
  readonly acctSnapshot: string;
  readonly acctSignOut: string;
  readonly acctNoLibraryNote: string;
  readonly acctWaiting: string;
}

export const ADMIN_RESTRICTED_LABELS: Readonly<Record<MarketplaceLanguage, AdminRestrictedLabels>> =
  Object.freeze({
    en: {
      loginTitle: 'QX Admin Console',
      loginSubtitle: 'Restricted to QX platform administrators',
      loginBadge: 'Restricted access',
      loginEmail: 'Administrator email',
      loginPassword: 'Password',
      loginSubmit: 'Sign in to console',
      loginPending: 'Verifying…',
      loginShow: 'Show',
      loginHide: 'Hide',
      loginNoSignup:
        'There is no sign-up here. Administrator accounts are provisioned on the hub and cannot be created from this page.',
      loginNotAdmin: 'This account is not a QX administrator. The session was closed.',
      loginFailed: 'Sign in failed.',
      loginSessionNote: 'A reader or publisher session is active. Signing in here replaces it.',
      loginBackStore: 'Back to store',

      acctBadge: 'QX Platform Administrator',
      acctConsoleTitle: 'Admin console',
      acctConsoleBody: 'Five monitoring sections, all reading live hub figures.',
      acctOpenConsole: 'Open',
      acctInsightTitle: 'Management insight',
      acctInsightBody:
        'Figures come from the hub dashboard snapshot below — never from the client.',
      acctSessionTitle: 'Session & security',
      acctSessionBody: 'Identity of this console session and how it was opened.',
      acctAccountId: 'Account ID',
      acctHub: 'Hub endpoint',
      acctSignInMethod: 'Sign-in method',
      acctSnapshot: 'Dashboard snapshot',
      acctSignOut: 'Sign out of console',
      acctNoLibraryNote:
        'Administrator accounts hold no library, orders or reading entitlements. This is the official QX operations identity.',
      acctWaiting: 'Waiting for the hub dashboard…',
    },
    ms: {
      loginTitle: 'Konsol Admin QX',
      loginSubtitle: 'Terhad kepada pentadbir platform QX',
      loginBadge: 'Akses terhad',
      loginEmail: 'E-mel pentadbir',
      loginPassword: 'Kata laluan',
      loginSubmit: 'Masuk ke konsol',
      loginPending: 'Mengesahkan…',
      loginShow: 'Papar',
      loginHide: 'Sembunyi',
      loginNoSignup:
        'Tiada pendaftaran di sini. Akaun pentadbir disediakan pada hub dan tidak boleh dicipta daripada halaman ini.',
      loginNotAdmin: 'Akaun ini bukan pentadbir QX. Sesi telah ditutup.',
      loginFailed: 'Log masuk gagal.',
      loginSessionNote:
        'Sesi pembaca atau penerbit sedang aktif. Log masuk di sini menggantikannya.',
      loginBackStore: 'Kembali ke kedai',

      acctBadge: 'Pentadbir Platform QX',
      acctConsoleTitle: 'Konsol pentadbir',
      acctConsoleBody: 'Lima seksyen pemantauan, semuanya membaca angka hub yang hidup.',
      acctOpenConsole: 'Buka',
      acctInsightTitle: 'Insight pengurusan',
      acctInsightBody:
        'Angka berasal daripada papar-masa dashboard hub di bawah — bukan daripada klien.',
      acctSessionTitle: 'Sesi & keselamatan',
      acctSessionBody: 'Identiti sesi konsol ini dan bagaimana ia dibuka.',
      acctAccountId: 'ID akaun',
      acctHub: 'Titik akhir hub',
      acctSignInMethod: 'Kaedah log masuk',
      acctSnapshot: 'Papar-masa dashboard',
      acctSignOut: 'Log keluar konsol',
      acctNoLibraryNote:
        'Akaun pentadbir tidak mempunyai perpustakaan, pesanan atau hak bacaan. Ini identiti operasi rasmi QX.',
      acctWaiting: 'Menunggu dashboard hub…',
    },
  });
